<script lang="ts" setup>
import type { SizeVariants } from '~kui/types/kui';

interface Props {
  src: string;
  alt?: string;
  environmentImage?: string | null;
  size?: Extract<SizeVariants, 'md' | 'lg'>;
}

const props = withDefaults(defineProps<Props>(), {
  alt: '',
  environmentImage: '/images/kui/unified/badge-environment.hdr',
  size: 'md',
});

const emit = defineEmits<{
  error: [];
}>();

const sizeClassList = computed(() => {
  return twMerge(
    cva('relative', {
      variants: {
        size: {
          md: 'size-60',
          lg: 'size-72 sm:size-80',
        },
      },
    })({ size: props.size }),
  );
});

const { $logErrorMessage } = useNuxtApp();

// model-viewer is a browser-only web component — register it on the client
// and hold the element back until the custom element is defined.
const viewerReady = ref(false);
const modelLoaded = ref(false);

// Spin-in reveal (Apple Fitness award style): once the model loads, the badge
// fades/scales in, then turns twice, easing into a long gentle settle that
// hands off to the slow auto-rotate. Theta stays positive so the spin
// direction matches auto-rotate's. The camera waits at 45° so the pre-spin
// frame is identical to the spin's start angle (the spin total is turns +
// that same 45°).
const SPIN_TURNS = 2;
const SPIN_TOTAL_DEG = SPIN_TURNS * 360 + 45;
const SPIN_DURATION_MS = 2200;
const SPIN_DELAY_MS = 300; // matches the enter crossfade's duration-300
// Only show the skeleton if loading actually takes a moment — a fast (cached)
// load would otherwise flash it for a split second before the badge reveals.
const SKELETON_APPEAR_DELAY_MS = 300;
const ENTER_CAMERA_ORBIT = '45deg 90deg auto';
const REST_CAMERA_ORBIT = '0deg 90deg auto';

// Asymmetric easing: cubic ease-in covers the first half of the rotation,
// quintic ease-out stretches the second half into a long gentle settle. The
// 0.375 time split is where the two curves meet at equal speed.
const SPIN_EASE_SPLIT = 0.375;
const easeSpin = (progress: number) => {
  if (progress < SPIN_EASE_SPLIT) {
    return 0.5 * (progress / SPIN_EASE_SPLIT) ** 3;
  }
  const settle = (progress - SPIN_EASE_SPLIT) / (1 - SPIN_EASE_SPLIT);
  return 0.5 + 0.5 * (1 - (1 - settle) ** 5);
};

type ModelViewerElement = HTMLElement & {
  cameraOrbit: string;
  jumpCameraToGoal?: () => void;
};

const viewerEl = ref<ModelViewerElement | null>(null);
const cameraOrbit = ref(ENTER_CAMERA_ORBIT);

const skeletonVisible = ref(false);
const prefersReducedMotion = ref(false);

let skeletonTimer: ReturnType<typeof setTimeout> | undefined;
let revealTimer: ReturnType<typeof setTimeout> | undefined;
let spinTimer: ReturnType<typeof setTimeout> | undefined;
let spinFrame: number | undefined;

onMounted(async () => {
  prefersReducedMotion.value = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  if (prefersReducedMotion.value) {
    cameraOrbit.value = REST_CAMERA_ORBIT;
  }
  skeletonTimer = setTimeout(() => {
    skeletonVisible.value = true;
  }, SKELETON_APPEAR_DELAY_MS);
  try {
    await import('@google/model-viewer');
    viewerReady.value = true;
  }
  catch {
    $logErrorMessage?.(`KUIUnifiedBadgePremium: model-viewer library failed to load (src: ${props.src})`);
    emit('error');
  }
});

const handleError = () => {
  $logErrorMessage?.(`KUIUnifiedBadgePremium: 3D model failed to load, falling back to static badge (src: ${props.src})`);
  emit('error');
};

const startSpin = () => {
  const el = viewerEl.value;
  if (!el) {
    return;
  }
  let cancelled = false;
  // Hand the camera over if the user grabs it mid-spin.
  const onCameraChange = (event: Event) => {
    if ((event as CustomEvent<{ source?: string }>).detail?.source === 'user-interaction') {
      cancelled = true;
    }
  };
  el.addEventListener('camera-change', onCameraChange);
  let start: number | undefined;
  const frame = (now: number) => {
    if (cancelled) {
      el.removeEventListener('camera-change', onCameraChange);
      return;
    }
    start ??= now;
    const progress = Math.min((now - start) / SPIN_DURATION_MS, 1);
    el.cameraOrbit = `${SPIN_TOTAL_DEG * (1 - easeSpin(progress))}deg 90deg auto`;
    el.jumpCameraToGoal?.();
    if (progress < 1) {
      spinFrame = requestAnimationFrame(frame);
    }
    else {
      el.removeEventListener('camera-change', onCameraChange);
      cameraOrbit.value = REST_CAMERA_ORBIT;
    }
  };
  spinFrame = requestAnimationFrame(frame);
};

const revealBadge = () => {
  clearTimeout(skeletonTimer);
  modelLoaded.value = true;
  if (!prefersReducedMotion.value) {
    spinTimer = setTimeout(startSpin, SPIN_DELAY_MS);
  }
};

const handleLoad = () => {
  // Crossfade only once the canvas has actually presented a frame — fading
  // the badge in on the load event itself ramps its opacity while the WebGL
  // canvas is still blank, which leaves a visible hole in the crossfade.
  // 'poster-dismissed' fires after model-viewer's first paint; the timeout is
  // a fallback so a missed event can't leave the skeleton stuck.
  let revealed = false;
  const reveal = () => {
    if (revealed) {
      return;
    }
    revealed = true;
    requestAnimationFrame(() => requestAnimationFrame(revealBadge));
  };
  viewerEl.value?.addEventListener('poster-dismissed', reveal, { once: true });
  revealTimer = setTimeout(reveal, 300);
};

onBeforeUnmount(() => {
  clearTimeout(skeletonTimer);
  clearTimeout(revealTimer);
  clearTimeout(spinTimer);
  if (spinFrame !== undefined) {
    cancelAnimationFrame(spinFrame);
  }
});
</script>

<template>
  <div :class="sizeClassList">
    <!-- Rounded-hexagon placeholder matching the badge silhouette. On load it
         crossfades with the badge in place — both transition simultaneously
         with mirrored 90⇄100 scales, so combined opacity stays ~constant (no
         empty-frame hole) and the silhouettes track (no double-exposure) -->
    <svg
      viewBox="0 0 200 230"
      class="pointer-events-none absolute inset-0 mx-auto h-full fill-muted p-4 transition duration-300 ease-out motion-reduce:transition-none"
      :class="skeletonVisible && !modelLoaded ? 'animate-pulse scale-100 opacity-100' : 'scale-90 opacity-0'"
      aria-hidden="true"
      data-test-id="kui-unified-badge-premium-skeleton"
    >
      <path d="M 117.15 14.9 L 178.11 50.1 Q 195.26 60 195.26 79.8 L 195.26 150.2 Q 195.26 170 178.11 179.9 L 117.15 215.1 Q 100 225 82.85 215.1 L 21.89 179.9 Q 4.74 170 4.74 150.2 L 4.74 79.8 Q 4.74 60 21.89 50.1 L 82.85 14.9 Q 100 5 117.15 14.9 Z" />
    </svg>
    <model-viewer
      v-if="viewerReady"
      ref="viewerEl"
      :src="src"
      :alt="alt"
      loading="eager"
      :auto-rotate="prefersReducedMotion ? undefined : true"
      auto-rotate-delay="0"
      camera-controls
      :camera-orbit="cameraOrbit"
      min-camera-orbit="auto 90deg auto"
      max-camera-orbit="auto 90deg auto"
      :environment-image="environmentImage ?? undefined"
      class="size-full transition duration-300 ease-out motion-reduce:transition-none"
      :class="modelLoaded ? 'scale-100 opacity-100' : 'scale-90 opacity-0'"
      data-test-id="kui-unified-badge-premium-viewer"
      @load="handleLoad"
      @error="handleError"
    >
      <!-- Replace model-viewer's built-in progress bar — the hexagon skeleton is our loading UI -->
      <!-- eslint-disable-next-line vue/no-deprecated-slot-attribute -- native shadow-DOM slot on a web component, not a Vue slot -->
      <div slot="progress-bar" />
    </model-viewer>
  </div>
</template>
